import { Link } from "react-router-dom";
import PageMeta from "../components/PageMeta";
import { Button } from "../components/ui/button";
import {
  Storefront, CalendarCheck, MapPin, UsersThree, Lightning, DeviceMobile, ArrowRight,
} from "@phosphor-icons/react";

const SECTIONS = [
  {
    icon: Storefront,
    title: "1. Set up your truck profile",
    testid: "guide-profile",
    cta: { label: "Open profile editor", to: "/dashboard/profile-editor" },
    points: [
      "Add your logo, cover photo, cuisine tags and a short description — profiles with a logo get tapped far more often.",
      "Set your service area (e.g. \"Outer Banks — Corolla to Hatteras\") so you show up in local searches and market pages.",
      "Upload your menu and photos, then hit Publish. Complete profiles rank higher in the truck directory.",
    ],
  },
  {
    icon: CalendarCheck,
    title: "2. Post your schedule",
    testid: "guide-schedule",
    cta: { label: "Post a schedule", to: "/dashboard/schedule-post" },
    points: [
      "Post today's spot or your whole week in one go — each stop needs a location, date and serving hours.",
      "Type any address or venue name and we geocode it to an exact map pin automatically.",
      "Plans change? Edit, cancel or delete any stop from your dashboard — followers see updates instantly.",
    ],
  },
  {
    icon: MapPin,
    title: "3. Go live on the map",
    testid: "guide-map",
    cta: { label: "See the live map", to: "/map" },
    points: [
      "The moment you post, your truck appears on the live map with your logo as the pin.",
      "During your serving window you show as \"open now\" — customers get one-tap directions to you.",
      "Your stops also appear on regional pages like the Outer Banks page, which ranks on Google.",
    ],
  },
  {
    icon: UsersThree,
    title: "4. Grow your following",
    testid: "guide-followers",
    points: [
      "Customers can follow your truck — every follower gets a push notification when you post a new stop.",
      "Share your public page link (thetrucktrackr.com/t/your-truck) on Instagram, Facebook and your window decal.",
      "The more you post, the more you appear in \"serving today\" lists — consistency wins.",
    ],
  },
  {
    icon: Lightning,
    title: "5. Put Pro to work",
    testid: "guide-pro",
    cta: { label: "Open Marketing Kit", to: "/dashboard/marketing-kit" },
    points: [
      "Marketing Kit: generate print-ready flyers and QR codes built from your profile in one click.",
      "Catering & event leads: locals request trucks for events through TruckTrackr — Pro trucks get the inquiries.",
      "Sync your schedule to Google or Outlook calendar, and track profile views from your dashboard.",
    ],
  },
  {
    icon: DeviceMobile,
    title: "6. Get the app",
    testid: "guide-app",
    points: [
      "TruckTrackr is on the App Store and Google Play — post a stop from your phone in under a minute.",
      "Everything syncs: post from the truck, edit from your laptop, your followers stay up to date.",
    ],
  },
];

export default function OwnerGuide() {
  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-10" data-testid="owner-guide-page">
      <PageMeta
        title="Owner Guide — How to Use TruckTrackr as a Food Truck | TruckTrackr"
        description="Step-by-step guide for food truck owners: set up your profile, post your schedule, go live on the map, grow followers, and use Pro tools like the Marketing Kit."
        canonical="https://thetrucktrackr.com/owner-guide"
      />
      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-primary mb-2">For truck owners</p>
      <h1 className="text-4xl sm:text-5xl font-bold font-display mb-3">The TruckTrackr Owner Guide</h1>
      <p className="text-base text-muted-foreground max-w-2xl mb-10">
        Everything you need to go from signup to a steady line at your window — in six short steps.
      </p>

      <div className="space-y-6">
        {SECTIONS.map(({ icon: Icon, title, points, cta, testid }) => (
          <section key={testid} className="rounded-2xl border border-border bg-card p-6" data-testid={testid}>
            <div className="flex items-center gap-3 mb-3">
              <span className="flex-none h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                <Icon size={20} weight="bold" />
              </span>
              <h2 className="text-lg md:text-lg font-semibold font-display">{title}</h2>
            </div>
            <ul className="space-y-2 text-sm text-muted-foreground list-disc pl-5">
              {points.map((p, i) => <li key={i}>{p}</li>)}
            </ul>
            {cta && (
              <Link to={cta.to} className="inline-flex items-center gap-1 mt-4 text-sm font-semibold text-primary hover:underline">
                {cta.label} <ArrowRight size={14} weight="bold" />
              </Link>
            )}
          </section>
        ))}
      </div>

      <div className="mt-10 rounded-2xl bg-primary/5 border border-primary/30 p-6 text-center" data-testid="guide-footer-cta">
        <h2 className="text-lg md:text-lg font-semibold font-display mb-1">Ready to roll?</h2>
        <p className="text-sm text-muted-foreground mb-4">Post your first schedule and you're live on the map today.</p>
        <Link to="/dashboard/schedule-post">
          <Button className="rounded-full" data-testid="guide-post-schedule-btn">Post my schedule</Button>
        </Link>
      </div>
    </div>
  );
}
